import React from 'react';
import { Popconfirm, Row, Tag, Tooltip } from 'antd';
import { PiPencil } from 'react-icons/pi';
import { IoEarth } from 'react-icons/io5';
import { Delete } from '@/lib/reactIconly';
import { Eye } from 'iconsax-react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { RiErrorWarningLine } from 'react-icons/ri';
import isEmpty from 'lodash/isEmpty';
import { Link } from 'react-router';
import dayjs from '../../../../lib/dayjsExtended';
import { IntlMessages } from '@/components';
import { HasRights, PrivateComponent } from '../../../../components/HasRights/HasRights';
import { renderDatetimeColumnWithUtcZeroTimeBool } from '../../../../lib/renderDatetimeColumnWithUtcZeroTimeBool';
import { renderReleasedToColumn } from '../../../../lib/renderReleasedToColumn';
import { Permissions } from '../../../../const/permissions';
import { DATE_TIME_FORMAT, EMPTY_IMAGE_PLACEHOLDER } from '../../../../const/system';
import { EVENT_TYPES_MAP } from '../../../../const/sources';
import appRoutes from '@/configs/routes';
import getFormattedDate from '../../../../lib/getFormattedDate';
import getDictionaryIdFromDynamicData from '../../../../lib/getDictionaryIdFromDynamicData';
import PublishedTag from '../../../../components/PublishedTag';
import StatusTag from '../../../../components/StatusTag';
import UnpublishedChangesWarningTitle from '../../../../components/UnpublishedChangesWarningTitle';
import styles from './PageFeatures.module.css';

export const FEATURES_SOURCE_MAP = {
  DAILY: 'daily',
  EDITOR_CHOICE: 'editors-choice',
  EXTERNAL_PROMO: 'external-promo',
};

export const FEATURES_TYPES_MAP = {
  [FEATURES_SOURCE_MAP.EXTERNAL_PROMO]: Object.values(EVENT_TYPES_MAP),
};

export const getFeaturesTableColumns = (
  handlePreviewDictionary,
  handleDelete,
  handleCardClick,
  featureConfig,
  featureSource,
  featureSourceKey,
  tableSize,
  urlPrefix,
  featureType,
  showImageAsPreview = false,
) => [
  // NOSONAR
  {
    title: <IntlMessages id="ui-general-id" />,
    dataIndex: 'id',
    align: 'center',
    width: tableSize.width * 0.05,
    sorter: true,
  },
  {
    title: <IntlMessages id="ui-general-published" />,
    align: 'center',
    dataIndex: 'is_published',
    sorter: true,
    width: tableSize.width * 0.13,
    render: (value, item) => <PublishedTag published={Boolean(value)} publishedDate={item.published_at} />,
  },
  {
    title: <IntlMessages id="ui-general-preview" />,
    align: 'center',
    dataIndex: 'image_id',
    width: tableSize.width * 0.13,
    sorter: showImageAsPreview,
    render: (value, item) => {
      const isImage = !isEmpty(item.images);

      return (
        <div style={{ display: 'inline-grid' }}>
          <LazyLoadImage
            onClick={() => {
              if (!isEmpty(item?.preview)) {
                handleCardClick({ isImage, data: isImage ? item.images.data[0] : item.preview });
              }
            }}
            style={!isEmpty(item?.preview) ? { cursor: 'pointer' } : {}}
            height={90}
            className="hp-cursor-pointer"
            effect="blur"
            alt="features img preview"
            placeholderSrc={EMPTY_IMAGE_PLACEHOLDER}
            src={item?.preview ? `${item?.preview}?time=${Date.now()}` : EMPTY_IMAGE_PLACEHOLDER}
          />
          {isImage && !isEmpty(item?.images?.data[0]) && (
            <Tooltip
              placement="right"
              title={() => {
                return (
                  <div>
                    <IntlMessages id="features-form-image-id-label" />
                    :&nbsp;
                    {item?.images?.data[0]?.id}
                  </div>
                );
              }}
            >
              <Tag className={styles.tag} color="default">
                {item?.images?.data[0]?.id}
              </Tag>
            </Tooltip>
          )}
        </div>
      );
    },
  },
  {
    title: <IntlMessages id="ui-general-title" />,
    dataIndex: 'title',
    width: tableSize.width * 0.10,
    align: 'center',
    render: (item, value) => <UnpublishedChangesWarningTitle text={item} data={value} />,
  },
  ...(featureConfig?.config?.show_in_featured_enabled
    ? [
        {
          title: <IntlMessages id="features-table-featured-column" />,
          dataIndex: 'is_in_featured',
          align: 'center',
          width: tableSize.width * 0.16,
          render: (value) => <StatusTag active={Boolean(value)} />,
        },
      ]
    : []),
  {
    title: <IntlMessages id="ui-general-updated-at" />,
    dataIndex: 'updated_at',
    align: 'center',
    sorter: true,
    width: tableSize.width * 0.12,
    render: (value) => getFormattedDate(value, DATE_TIME_FORMAT),
  },
  {
    title: <IntlMessages id="ui-general-released-at" />,
    dataIndex: 'released_at',
    align: 'center',
    sorter: true,
    width: tableSize.width * 0.12,
    render: (value, item) => renderDatetimeColumnWithUtcZeroTimeBool(value, item),
  },
  ...(featureConfig?.config?.release_range
    ? [
        {
          title: <IntlMessages id="ui-general-released-to" />,
          dataIndex: 'released_to',
          align: 'center',
          sorter: true,
          width: tableSize.width * 0.12,
          render: renderReleasedToColumn,
        },
      ]
    : []),
  ...(HasRights([
    Permissions.FEATURED.FEATURES[featureSourceKey].FIND,
    Permissions.FEATURED.FEATURES[featureSourceKey].EDIT,
    Permissions.FEATURED.FEATURES[featureSourceKey].DELETE,
    Permissions.TRANSLATIONS.DICTIONARIES.DETAILS.VIEW,
  ])
    ? [
        {
          title: <IntlMessages id="ui-general-action" />,
          key: 'action',
          fixed: 'right',
          align: 'center',
          width: tableSize.width * 0.10,
          render: (item) => {
            const dictionaryId = getDictionaryIdFromDynamicData(item?.dynamic_data?.data);
            const canEditPastItem = featureConfig?.config?.publish?.allow_update_past ?? true;
            const canEditItem =
              canEditPastItem || !item?.released_at
                ? true
                : dayjs().isSameOrBefore(dayjs(item.released_at).subtract(-1, 'days'), 'day');

            return (
              <Row className="da-h-100" align="middle" justify="center">
                <PrivateComponent allowedPermissions={[Permissions.FEATURED.FEATURES[featureSourceKey].FIND]}>
                  <Tooltip placement="top" title={<IntlMessages id="ui-general-view" />}>
                    <div className="hp-text-right">
                      <Link
                        to={appRoutes.urlPageFeatureDetails({
                          urlPrefix,
                          featureType,
                          featureSource,
                          featureId: item.id,
                        })}
                        target="_blank"
                      >
                        <Eye
                          color="currentColor"
                          size={20}
                          data-testid="view-icon"
                          className="hp-cursor-pointer hp-transition hp-hover-text-color-primary-1 hp-text-color-black-80 hp-m-4"
                        />
                      </Link>
                    </div>
                  </Tooltip>
                </PrivateComponent>
                <PrivateComponent allowedPermissions={[Permissions.FEATURED.FEATURES[featureSourceKey].EDIT]}>
                  {canEditItem && (
                    <Tooltip placement="top" title={<IntlMessages id="ui-general-edit" />}>
                      <div className="hp-text-right">
                        <Link
                          to={appRoutes.urlPageFeaturesEdit({
                            urlPrefix,
                            featureType,
                            featureSource,
                            featureId: item.id,
                          })}
                        >
                          <PiPencil
                            size={20}
                            data-testid="edit-icon"
                            className="hp-cursor-pointer hp-transition hp-hover-text-color-warning-1 hp-text-color-black-80 hp-m-4"
                          />
                        </Link>
                      </div>
                    </Tooltip>
                  )}
                </PrivateComponent>
                <PrivateComponent
                  allowedPermissions={[Permissions.FEATURED.FEATURES[featureSourceKey].DELETE]}
                >
                  {!item.is_published ? (
                    <Popconfirm
                      title={<IntlMessages id="ui-general-delete-confirm-message" />}
                      placement="topLeft"
                      onConfirm={() => handleDelete(item.id)}
                      okText={<IntlMessages id="ui-general-yes" />}
                      cancelText={<IntlMessages id="ui-general-no" />}
                      icon={<RiErrorWarningLine className="remix-icon hp-text-color-primary-1" />}
                      okButtonProps={{ danger: true }}
                    >
                      <Tooltip placement="top" title={<IntlMessages id="ui-general-delete" />}>
                        <div className="hp-text-right">
                          <Delete
                            size={20}
                            data-testid="delete-icon"
                            className="hp-cursor-pointer hp-transition hp-hover-text-color-danger-1 hp-text-color-black-80 hp-m-4"
                          />
                        </div>
                      </Tooltip>
                    </Popconfirm>
                  ) : (
                    <Tooltip
                      placement="top"
                      title={<IntlMessages id="ui-general-delete-published-notification" />}
                    >
                      <div className="hp-text-right">
                        <Delete
                          disabled
                          size={20}
                          data-testid="delete-icon"
                          className="hp-opacity-4 hp-cursor-pointer hp-transition hp-text-color-black-80 hp-m-4"
                        />
                      </div>
                    </Tooltip>
                  )}
                </PrivateComponent>
                <PrivateComponent allowedPermissions={[Permissions.TRANSLATIONS.DICTIONARIES.DETAILS.VIEW]}>
                  {dictionaryId ? (
                    <Tooltip placement="top" title={<IntlMessages id="ui-general-dictionary" />}>
                      <div className="hp-text-right">
                        <IoEarth
                          size={20}
                          data-testid="dictionary-icon"
                          onClick={() => handlePreviewDictionary(dictionaryId)}
                          className="hp-cursor-pointer hp-transition hp-hover-text-color-primary-1 hp-text-color-black-80 hp-m-4"
                        />
                      </div>
                    </Tooltip>
                  ) : (
                    <Tooltip placement="top" title={<IntlMessages id="dictionaries-empty-notification" />}>
                      <div className="hp-text-right">
                        <IoEarth
                          size={20}
                          disabled
                          data-testid="dictionary-icon"
                          className="hp-opacity-4 hp-cursor-pointer hp-transition hp-hover-text-color-primary-1 hp-text-color-black-80 hp-m-4"
                        />
                      </div>
                    </Tooltip>
                  )}
                </PrivateComponent>
              </Row>
            );
          },
        },
      ]
    : []),
];
